import { BadgeCheck, Mail, Phone, AtSign } from "lucide-react";
import { Stars } from "@/components/site/Stars";

const Btn = ({ children, onClick, tone = "ghost", testid }) => {
  const tones = {
    ok: "bg-emerald-400 text-[#050713] hover:bg-emerald-300",
    bad: "border border-rose-400/40 text-rose-200 hover:bg-rose-500/15",
    ghost: "border border-white/15 text-slate-200 hover:bg-white/10",
  };
  return <button onClick={onClick} data-testid={testid} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${tones[tone]}`}>{children}</button>;
};

const Pill = ({ children, tone }) => (
  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-mono uppercase tracking-wider ${tone === "pending" ? "bg-amber-400/15 text-amber-200" : tone === "rejected" || tone === "hidden" ? "bg-rose-400/15 text-rose-200" : "bg-emerald-400/15 text-emerald-200"}`}>{children}</span>
);

const Empty = ({ a }) => <p className="mt-6 text-slate-400">{a.none}</p>;
const date = (iso) => new Date(iso).toLocaleString();
const ORDER = { pending: 0, approved: 1, published: 1, hidden: 2, rejected: 2 };
const sortByStatus = (arr) => [...arr].sort((x, y) => (ORDER[x.status] ?? 3) - (ORDER[y.status] ?? 3));

export const UsersTab = ({ items, a, act }) => (
  <div className="mt-6 space-y-3">
    {items.length === 0 && <Empty a={a} />}
    {sortByStatus(items).map((u) => (
      <div key={u.user_id} className="bento-card rounded-2xl p-4 md:p-5 flex flex-wrap items-center gap-4" data-testid={`admin-user-${u.user_id}`}>
        {u.picture ? <img src={u.picture} alt="" className="w-10 h-10 rounded-full" referrerPolicy="no-referrer" /> : <span className="w-10 h-10 rounded-full bg-violet-500/30" />}
        <div className="min-w-0 flex-1">
          <p className="text-white font-semibold truncate">{u.name}</p>
          <p className="text-sm text-slate-400 truncate">{u.email} · {date(u.created_at)}</p>
        </div>
        <Pill tone={u.status}>{a.status[u.status]}</Pill>
        <div className="flex gap-2">
          {u.status !== "approved" && <Btn tone="ok" testid={`approve-user-${u.user_id}`} onClick={() => act("patch", `/admin/users/${u.user_id}`, { status: "approved" })}>{a.approve}</Btn>}
          {u.status === "pending" && <Btn tone="bad" testid={`reject-user-${u.user_id}`} onClick={() => act("patch", `/admin/users/${u.user_id}`, { status: "rejected" })}>{a.reject}</Btn>}
          {u.status === "approved" && <Btn tone="bad" testid={`revoke-user-${u.user_id}`} onClick={() => act("patch", `/admin/users/${u.user_id}`, { status: "rejected" })}>{a.revoke}</Btn>}
        </div>
      </div>
    ))}
  </div>
);

export const ReviewsTab = ({ items, a, act }) => (
  <div className="mt-6 grid md:grid-cols-2 gap-3">
    {items.length === 0 && <Empty a={a} />}
    {sortByStatus(items).map((r) => (
      <div key={r.review_id} className="bento-card rounded-2xl p-5 flex flex-col gap-3" data-testid={`admin-review-${r.review_id}`}>
        <div className="flex items-center justify-between gap-2">
          <Stars value={r.rating} />
          <Pill tone={r.status}>{a.status[r.status]}</Pill>
        </div>
        <p className="text-slate-200 text-sm leading-relaxed">{r.text}</p>
        <p className="text-sm text-slate-400 flex items-center gap-2">
          {r.name} · {r.member ? a.member : a.guest} {r.verified && <BadgeCheck className="w-4 h-4 text-sky-300" />}
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          {r.status !== "published" && <Btn tone="ok" testid={`publish-review-${r.review_id}`} onClick={() => act("patch", `/admin/reviews/${r.review_id}`, { status: "published", verified: true })}>{a.publish}</Btn>}
          {r.status === "published" && <Btn testid={`hide-review-${r.review_id}`} onClick={() => act("patch", `/admin/reviews/${r.review_id}`, { status: "hidden" })}>{a.hide}</Btn>}
          <Btn testid={`verify-review-${r.review_id}`} onClick={() => act("patch", `/admin/reviews/${r.review_id}`, { verified: !r.verified })}>{r.verified ? a.unverify : a.verify}</Btn>
          <Btn tone="bad" testid={`delete-review-${r.review_id}`} onClick={() => act("delete", `/admin/reviews/${r.review_id}`)}>{a.del}</Btn>
        </div>
      </div>
    ))}
  </div>
);

export const ContactsTab = ({ items, a, act }) => (
  <div className="mt-6 space-y-3">
    {items.length === 0 && <Empty a={a} />}
    {items.map((c) => (
      <div key={c.contact_id} className={`bento-card rounded-2xl p-5 ${c.read ? "opacity-75" : ""}`} data-testid={`admin-contact-${c.contact_id}`}>
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-white font-semibold">{c.name}</p>
          {!c.read && <Pill tone="pending">{a.unread}</Pill>}
          {c.emailed && <Pill tone="approved">{a.emailed}</Pill>}
          <span className="ml-auto text-xs text-slate-400 font-mono">{c.lang?.toUpperCase()} · {date(c.created_at)}</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-300">
          <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{c.email}</span>
          {c.phone && <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{c.phone}</span>}
          {c.social && <span className="flex items-center gap-1.5"><AtSign className="w-3.5 h-3.5" />{c.social}</span>}
        </div>
        <p className="mt-3 text-slate-200 text-sm leading-relaxed whitespace-pre-wrap">{c.message}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <a href={`mailto:${c.email}`} data-testid={`reply-contact-${c.contact_id}`} className="rounded-full px-3.5 py-1.5 text-xs font-semibold bg-sky-300 text-[#050713] hover:bg-sky-200 transition-colors">{a.reply}</a>
          {!c.read && <Btn testid={`read-contact-${c.contact_id}`} onClick={() => act("patch", `/admin/contacts/${c.contact_id}/read`)}>{a.markRead}</Btn>}
          <Btn tone="bad" testid={`delete-contact-${c.contact_id}`} onClick={() => act("delete", `/admin/contacts/${c.contact_id}`)}>{a.del}</Btn>
        </div>
      </div>
    ))}
  </div>
);
